import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { BillingSnapshot, billingApi, planLabel } from "@/lib/billing-api";
import { useAuth } from "@/lib/auth-context";
import { usePlan } from "@/lib/use-plan";
import { Skeleton } from "@/components/catalog/Skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui-shim";

const STARTER_FEATURES = [
  "Item cataloging with photo upload + custom fields",
  "Location tracking + movement history",
  "Advanced search and filtering",
  "Basic client profiles",
];

const PRO_FEATURES = [
  "Everything in Starter, plus:",
  "Barcode / QR code generation and scanning",
  "Outfit creation and lookbooks",
  "Loan / borrow tracking with return dates",
  "Reporting and analytics",
  "Audit log + role-based team access",
  "CSV bulk import",
  "Unlimited shareable lookbooks",
];

function formatCents(cents: number, currency = "usd") {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency, minimumFractionDigits: 0,
  }).format(cents / 100);
}

function statusPill(status: string, cancelAtPeriodEnd: boolean) {
  if (status === "active" && cancelAtPeriodEnd) return { label: "Canceling", tone: "warn" };
  if (status === "active") return { label: "Active", tone: "ok" };
  if (status === "trial") return { label: "Trial", tone: "neutral" };
  if (status === "past_due") return { label: "Past due", tone: "danger" };
  if (status === "canceled" || status === "expired") return { label: "Canceled", tone: "danger" };
  return { label: status, tone: "neutral" };
}

export default function BillingSettings() {
  const { user } = useAuth();
  const { refresh: refreshPlan } = usePlan();
  const [params, setParams] = useSearchParams();
  const [snap, setSnap] = useState<BillingSnapshot | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showCancel, setShowCancel] = useState(false);

  const isOwner = user?.role === "owner";

  async function load() {
    try {
      const s = await billingApi.snapshot();
      setSnap(s);
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not load billing.");
    }
  }
  useEffect(() => { load(); }, []);

  // Surface checkout success/cancel from URL params.
  useEffect(() => {
    const flag = params.get("checkout");
    if (flag === "success") {
      toast.success("Subscription updated.");
      setParams((p) => { p.delete("checkout"); return p; }, { replace: true });
      load();
      refreshPlan();
    } else if (flag === "cancel") {
      toast.info("Checkout cancelled.");
      setParams((p) => { p.delete("checkout"); return p; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  async function startCheckout(plan: "starter" | "pro") {
    if (!isOwner) {
      toast.error("Only the workspace owner can change plans.");
      return;
    }
    setBusy(`checkout-${plan}`);
    try {
      const r = await billingApi.checkout(plan);
      window.location.href = r.url;
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not start checkout.");
      setBusy(null);
    }
  }

  async function openPortal() {
    setBusy("portal");
    try {
      const r = await billingApi.portal();
      window.location.href = r.url;
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not open portal.");
      setBusy(null);
    }
  }

  async function cancel() {
    setBusy("cancel");
    try {
      await billingApi.cancel();
      toast.success("Subscription will end at the next renewal.");
      setShowCancel(false);
      await load(); await refreshPlan();
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not cancel.");
    } finally {
      setBusy(null);
    }
  }

  async function resume() {
    setBusy("resume");
    try {
      await billingApi.resume();
      toast.success("Subscription resumed.");
      await load(); await refreshPlan();
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not resume.");
    } finally {
      setBusy(null);
    }
  }

  if (!isOwner) {
    return (
      <div data-testid="billing-locked" className="border border-border bg-bone/40 p-8">
        <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-3">— Billing</p>
        <h2 className="font-display text-2xl">Owner-only</h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Plan changes and payment methods are managed by the workspace owner.
          Ask the owner to upgrade if you need access to a Pro feature.
        </p>
      </div>
    );
  }

  if (!snap) return <Skeleton className="h-96" />;

  const { effective_plan, subscription, invoices, card_brand, card_last4, prices, provider } = snap;
  const pill = statusPill(subscription.status, subscription.cancel_at_period_end);

  return (
    <div data-testid="billing-settings" className="space-y-12">
      {/* Current plan */}
      <section>
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Current plan</p>
            <h2 className="font-display text-3xl">{planLabel(effective_plan)}</h2>
          </div>
          <span data-testid="billing-status-pill"
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] ${
              pill.tone === "ok" ? "bg-emerald-50 text-emerald-800 border border-emerald-100"
                : pill.tone === "warn" ? "bg-amber-50 text-amber-800 border border-amber-100"
                : pill.tone === "danger" ? "bg-oxblood/10 text-oxblood border border-oxblood/30"
                : "bg-bone text-foreground border border-border"
            }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${
              pill.tone === "ok" ? "bg-emerald-500"
                : pill.tone === "warn" ? "bg-amber-500"
                : pill.tone === "danger" ? "bg-oxblood"
                : "bg-foreground/40"
            }`} />
            {pill.label}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Tile label="Renews">
            {subscription.cancel_at_period_end && subscription.current_period_end
              ? `Access until ${new Date(subscription.current_period_end).toLocaleDateString()}`
              : subscription.current_period_end
                ? new Date(subscription.current_period_end).toLocaleDateString()
                : subscription.trial_end
                  ? `Trial ends ${new Date(subscription.trial_end).toLocaleDateString()}`
                  : "—"}
          </Tile>
          <Tile label="Provider">{provider === "mock" ? "Mock (preview)" : "Stripe"}</Tile>
          <Tile label="Payment">
            {card_brand && card_last4 ? `${card_brand} •••• ${card_last4}` : "No card on file"}
          </Tile>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          {subscription.cancel_at_period_end ? (
            <Button onClick={resume} disabled={busy === "resume"}
              data-testid="billing-resume-btn"
              className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10">
              {busy === "resume" ? "Resuming…" : "Resume subscription"}
            </Button>
          ) : (subscription.status === "active" && (
            <>
              <Button variant="outline" onClick={openPortal} disabled={busy === "portal"}
                data-testid="billing-portal-btn"
                className="rounded-none border-foreground/20 hover:bg-bone h-10">
                <CreditCard className="w-4 h-4 mr-2" />
                {busy === "portal" ? "Opening…" : "Manage payment method"}
              </Button>
              <Button variant="outline" onClick={() => setShowCancel(true)}
                data-testid="billing-cancel-btn"
                className="rounded-none border-oxblood/40 text-oxblood hover:bg-oxblood/5 h-10">
                Cancel subscription
              </Button>
            </>
          ))}
        </div>
      </section>

      {/* Plan picker */}
      <section>
        <h2 className="font-display text-2xl">Plans</h2>
        <p className="text-sm text-muted-foreground mt-1">Switch any time. Pro-rates aren't enforced in this preview.</p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <PlanCard
            name="Starter" testid="plan-card-starter"
            price={prices.starter?.label || "$8 / month"}
            features={STARTER_FEATURES}
            current={effective_plan === "starter"}
            onSelect={() => startCheckout("starter")}
            busy={busy === "checkout-starter"} />
          <PlanCard
            name="Pro" testid="plan-card-pro" highlight
            price={prices.pro?.label || "$90 / month"}
            features={PRO_FEATURES}
            current={effective_plan === "pro"}
            onSelect={() => startCheckout("pro")}
            busy={busy === "checkout-pro"}
            currentLabel={undefined} />
        </div>
      </section>

      {/* Invoices */}
      <section>
        <h2 className="font-display text-2xl">Invoices</h2>
        {invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground italic mt-3">No invoices yet.</p>
        ) : (
          <ul className="border border-border divide-y divide-border bg-background mt-4"
            data-testid="invoices-list">
            {invoices.map((inv) => (
              <li key={inv.id} className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-3 text-sm">
                <span className="font-mono text-xs text-muted-foreground truncate">{inv.id}</span>
                <span className="tabular-nums">{formatCents(inv.amount_due, inv.currency)}</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {inv.status} · {new Date(inv.created_at).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {showCancel && (
        <Dialog open onOpenChange={(o: boolean) => !o && setShowCancel(false)}>
          <DialogContent className="rounded-none border-border" data-testid="cancel-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-oxblood">Cancel subscription</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Your access continues until{" "}
              <span className="text-foreground font-medium">
                {subscription.current_period_end
                  ? new Date(subscription.current_period_end).toLocaleDateString()
                  : "the next renewal"}
              </span>. You can resume any time before then.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCancel(false)}
                className="rounded-none border-foreground/20 hover:bg-bone h-10">
                Keep subscription
              </Button>
              <Button onClick={cancel} disabled={busy === "cancel"}
                data-testid="cancel-confirm"
                className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10">
                {busy === "cancel" ? "Cancelling…" : "Cancel at period end"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Tile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border border-border p-4 bg-background">
      <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</p>
      <p className="text-sm font-medium mt-1.5 truncate">{children}</p>
    </div>
  );
}

function PlanCard({
  name, price, features, current, currentLabel, onSelect, busy, highlight, testid,
}: {
  name: string; price: string; features: string[];
  current?: boolean; currentLabel?: string;
  onSelect: () => void; busy?: boolean; highlight?: boolean; testid: string;
}) {
  return (
    <div data-testid={testid}
      className={`relative border bg-background p-7 ${
        highlight ? "border-oxblood" : "border-border"
      }`}>
      {highlight && (
        <span className="absolute -top-2.5 left-7 px-2 py-0.5 bg-oxblood text-ivory text-[10px] uppercase tracking-[0.22em]">
          Most popular
        </span>
      )}
      <h3 className="font-display text-2xl">{name}</h3>
      <p className="mt-1.5 text-sm text-muted-foreground tabular-nums">{price}</p>
      <ul className="mt-5 space-y-2.5 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-oxblood mt-0.5 shrink-0" strokeWidth={1.8} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        type="button"
        onClick={onSelect}
        disabled={busy || current}
        data-testid={`${testid}-cta`}
        className={`w-full mt-7 rounded-none h-11 ${
          highlight
            ? "bg-oxblood hover:bg-oxblood-deep text-ivory disabled:bg-oxblood/40"
            : "bg-foreground hover:bg-charcoal text-ivory disabled:bg-foreground/40"
        }`}>
        {current
          ? (currentLabel || "Current plan")
          : busy
            ? "Redirecting…"
            : `Switch to ${name}`}
      </Button>
    </div>
  );
}
